import { signal } from '@angular/core';
import type { UIMessage } from 'ai';
import { v4 } from 'uuid';
import { Subscription } from 'rxjs';
import { AI_CHAT_CONTEXT_GUARD_METADATA_KEY, AI_CHAT_CONTEXT_LIMIT_REACHED_CODE, AI_PROVIDER_UNAVAILABLE_CODE, isAIChatContextGuardStatus } from '@jms/model';
import type { AIAgentAttachment, AIAgentClientContext, AIChatContextGuardStatus, AIInterviewResponse, MastraAgentStreamEvent } from '@jms/model';
import { AIService } from '@workbuddy/ai/core/ai.service';
import { AgentUtility } from '../agent.utility';
import type { AgentModelTier, AnyPart } from '../agent.types';

type ChatStatus = 'idle' | 'submitted' | 'streaming';

interface NativeTurnRequest {
    userMessage: UIMessage;
    threadId?: string | null;
    interactionMode: 'interactive' | 'automation';
    modelTier: AgentModelTier;
    currentJobId?: string;
    currentWorkOrderId?: string;
    attachmentBlobUrls?: Record<string, string>;
}

interface ToolApprovalState {
    id?: string;
    runId?: string;
}

/**
 * Minimal client-side adapter for Workbuddy Agent's native agent stream.
 * Keeps the UI-facing `messages/status/error` contract that the Angular view expects,
 * but swaps the transport to `/api/ai/agents/workbuddy-agent/stream`.
 */
export class NativeWorkbuddyAgentChatAdapter {

    private readonly $messages = signal<UIMessage[]>([]);
    private readonly $status = signal<ChatStatus>('idle');
    private readonly $error = signal<Error | null>(null);
    private readonly $contextGuardStatus = signal<AIChatContextGuardStatus | null>(null);
    // The upstream model provider refused for its own quota/billing/capacity reasons (typed code
    // from the API relay). Rendered as a graceful notice, not the generic stream error; cleared on
    // the next send attempt or an explicit "Try again".
    private readonly $providerUnavailable = signal(false);
    // Count of sends refused by the API's usage-limit door check (HTTP 429). The host watches this
    // and re-reads the limit status, so the blocked composer notice appears instead of an error.
    private readonly $usageLimitRejections = signal(0);
    private readonly pendingApprovalToolCalls = new Set<string>();
    private readonly toolRunIds = new Map<string, string>();
    private readonly toolInputBuffers = new Map<string, string>();
    private activeSubscription: Subscription | null = null;
    private activeAbortController: AbortController | null = null;
    private activeCompletion: (() => void) | null = null;
    private currentRunId: string | null = null;
    private pendingTurnUserMessageId: string | null = null;
    private pendingTurnAssistantMessageId: string | null = null;
    private resolvingAbort = false;

    constructor(private readonly aiService: AIService) {}

    public get messages(): UIMessage[] {

        return this.$messages();
    }

    public set messages(messages: UIMessage[]) {

        const cloned = messages.map(message => this.cloneMessage(message));
        this.$messages.set(cloned);
        this.restorePendingApprovalRunIds(cloned);
        this.$contextGuardStatus.set(this.readContextGuardStatus(cloned));
    }

    public get status(): ChatStatus {

        return this.$status();
    }

    public get error(): Error | null {

        return this.$error();
    }

    public get contextGuardStatus(): AIChatContextGuardStatus | null {

        return this.$contextGuardStatus();
    }

    public get providerUnavailable(): boolean {

        return this.$providerUnavailable();
    }

    public get usageLimitRejections(): number {

        return this.$usageLimitRejections();
    }

    public clearProviderUnavailable(): void {

        this.$providerUnavailable.set(false);
    }

    public get hasPendingApproval(): boolean {

        return this.pendingApprovalToolCalls.size > 0;
    }

    public get lastMessage(): UIMessage | undefined {

        const messages = this.$messages();
        return messages[messages.length - 1];
    }

    public clearError(): void {

        this.$error.set(null);
    }

    public resetTransientState(): void {

        this.abortActiveStream();
        this.pendingApprovalToolCalls.clear();
        this.toolRunIds.clear();
        this.toolInputBuffers.clear();
        this.currentRunId = null;
        this.pendingTurnUserMessageId = null;
        this.pendingTurnAssistantMessageId = null;
        this.$status.set('idle');
        this.$error.set(null);
        this.$providerUnavailable.set(false);
    }

    public async stop(): Promise<void> {

        this.resetTransientState();
    }

    public async sendTurn(request: NativeTurnRequest): Promise<void> {

        this.resetTransientState();
        const sanitizedMessages = AgentUtility.sanitizeOutboundMessages(this.$messages());
        this.$messages.set(sanitizedMessages);

        const userMessage = this.cloneMessage(request.userMessage);
        const assistantPlaceholder = this.createAssistantPlaceholder();
        const nextMessages = [...sanitizedMessages, userMessage, assistantPlaceholder];
        this.pendingTurnUserMessageId = userMessage.id;
        this.pendingTurnAssistantMessageId = assistantPlaceholder.id;
        this.$messages.set(nextMessages);
        this.$status.set('submitted');

        this.activeAbortController = new AbortController();
        const bodyOptions: AIAgentClientContext = {
            interactionMode: request.interactionMode,
            modelTier: request.modelTier,
            currentJobId: request.currentJobId,
            currentWorkOrderId: request.currentWorkOrderId,
            attachmentBlobUrls: request.attachmentBlobUrls,
            attachedFiles: this.extractAttachedFiles(userMessage, request.attachmentBlobUrls),
        };

        const stream = this.aiService.streamNativeAgent(
            AgentUtility.AGENT_ID,
            [userMessage],
            request.threadId ?? undefined,
            this.activeAbortController.signal,
            false,
            bodyOptions
        );

        await this.consumeStream(stream);
    }

    public async approveToolCall(toolCallId: string, interviewResponse?: AIInterviewResponse): Promise<boolean> {

        const runId = this.resolveRunId(toolCallId);
        if (!runId) {
            this.reportUnresolvableApproval();
            return false;
        }

        this.detachActiveStream();
        this.updateToolPart(toolCallId, { state: 'approval-responded' });
        this.$status.set('streaming');
        this.clearError();

        this.activeAbortController = new AbortController();
        const stream = this.aiService.approveToolCall(AgentUtility.AGENT_ID, runId, toolCallId, this.activeAbortController.signal, interviewResponse);
        // The resume stream is consumed in the background so callers learn the dispatch decision this
        // frame (enabling the optimistic card dismiss) without waiting for the whole turn to finish.
        void this.consumeStream(stream, toolCallId);
        return true;
    }

    public async declineToolCall(toolCallId: string, reason?: string): Promise<boolean> {

        const runId = this.resolveRunId(toolCallId);
        if (!runId) {
            this.reportUnresolvableApproval();
            return false;
        }

        this.detachActiveStream();
        this.updateToolPart(toolCallId, { state: 'approval-responded' });
        this.$status.set('streaming');
        this.clearError();

        this.activeAbortController = new AbortController();
        const stream = this.aiService.declineToolCall(AgentUtility.AGENT_ID, runId, toolCallId, reason, this.activeAbortController.signal);
        // Consumed in the background — see approveToolCall for why the dispatch decision returns early.
        void this.consumeStream(stream, toolCallId, reason);
        return true;
    }

    /**
     * A restored `approval-requested` part can lose its run id (persisted only conditionally),
     * leaving the approval with no resumable run. Surface the failure through the same `$error`
     * stream surface the UI already renders (danger alert + Retry) instead of silently no-op'ing.
     */
    private reportUnresolvableApproval(): void {

        this.$error.set(new Error('Could not resume this approval. Please refresh the page to continue.'));
    }

    private async consumeStream(stream: ReturnType<AIService['streamNativeAgent']>, pendingToolCallId?: string, declineReason?: string): Promise<void> {
        this.resolvingAbort = false;

        await new Promise<void>((resolve) => {
            this.activeCompletion = resolve;
            this.activeSubscription = stream.subscribe({
                next: (event) => this.handleStreamEvent(event),
                error: (error) => {
                    if (this.isUsageLimitRejection(error)) {
                        // The API's usage-limit door check refused before the stream started —
                        // this happens when a stale browser hasn't learned the tenancy is blocked
                        // yet. Not an error to show: roll back the optimistic turn and let the host
                        // re-read the limit status, which swaps the composer for the blocked notice.
                        this.rollbackRejectedTurn();
                        this.$usageLimitRejections.update(count => count + 1);
                    } else if (!this.resolvingAbort && !this.isAbortRelatedError(error)) {
                        this.$error.set(error instanceof Error ? error : new Error(String(error)));
                    }
                    this.finalizeStream(pendingToolCallId, declineReason);
                },
                complete: () => this.finalizeStream(pendingToolCallId, declineReason),
            });
        });
    }

    private finalizeStream(pendingToolCallId?: string, declineReason?: string): void {

        // Fallback for declines where the stream ended without a tool-output-denied event:
        // still land the part in the calm terminal output-denied state, with NO errorText so it
        // never renders as a red error. declineReason is intentionally not surfaced on the part.
        if (pendingToolCallId && declineReason && !this.hasOutputForToolCall(pendingToolCallId)) {
            this.updateToolPart(pendingToolCallId, { state: 'output-denied' });
            this.pendingApprovalToolCalls.delete(pendingToolCallId);
            this.toolRunIds.delete(pendingToolCallId);
        }

        this.cleanupEmptyAssistantPlaceholder();

        this.$status.set('idle');

        this.activeSubscription?.unsubscribe();
        this.activeSubscription = null;
        this.activeAbortController = null;
        this.pendingTurnUserMessageId = null;
        this.pendingTurnAssistantMessageId = null;
        this.resolvingAbort = false;

        const resolve = this.activeCompletion;
        this.activeCompletion = null;
        resolve?.();
    }

    private handleStreamEvent(event: MastraAgentStreamEvent): void {

        switch (event.type) {
            case 'start':
                this.currentRunId = event.runId ?? (event.payload as { id?: string } | undefined)?.id ?? this.currentRunId;
                this.$status.set('streaming');
                this.clearError();
                break;
            case 'text-start':
                this.appendTextPart('');
                break;
            case 'text-delta':
                this.appendTextDelta(event.payload.text);
                break;
            case 'reasoning-start':
                this.appendReasoningPart('');
                break;
            case 'reasoning-delta':
                this.appendReasoningDelta(event.payload.text);
                break;
            case 'tool-call-input-streaming-start':
                this.toolInputBuffers.set(event.payload.toolCallId, '');
                this.updateToolPart(event.payload.toolCallId, { toolName: event.payload.toolName, state: 'input-streaming', input: {} });
                break;
            case 'tool-call-delta':
                this.toolInputBuffers.set(event.payload.toolCallId, `${this.toolInputBuffers.get(event.payload.toolCallId) ?? ''}${event.payload.argsTextDelta}`);
                this.updateToolPart(event.payload.toolCallId, {
                    toolName: event.payload.toolName,
                    state: 'input-streaming',
                    input: this.toolInputBuffers.get(event.payload.toolCallId) ?? event.payload.argsTextDelta,
                });
                break;
            case 'tool-call-input-streaming-end': {
                const buffer = this.toolInputBuffers.get(event.payload.toolCallId) ?? '';
                this.toolInputBuffers.delete(event.payload.toolCallId);
                this.updateToolPart(event.payload.toolCallId, { state: 'input-available', input: this.parseToolInput(buffer) });
                break;
            }
            case 'tool-call':
                this.updateToolPart(event.payload.toolCallId, { toolName: event.payload.toolName, state: 'input-available', input: event.payload.args });
                break;
            case 'tool-call-approval': {
                const runId = event.runId ?? event.payload.runId ?? this.currentRunId ?? undefined;
                this.currentRunId = runId ?? this.currentRunId;
                this.pendingApprovalToolCalls.add(event.payload.toolCallId);
                if (runId) {
                    this.toolRunIds.set(event.payload.toolCallId, runId);
                }
                this.updateToolPart(event.payload.toolCallId, {
                    toolName: event.payload.toolName,
                    state: 'approval-requested',
                    input: event.payload.args,
                    approval: { id: event.payload.toolCallId, ...(runId ? { runId } : {}) },
                });
                this.$status.set('idle');
                break;
            }
            case 'tool-output-denied': {
                // A declined approval is a calm terminal state, NOT an error. Mastra puts the
                // identifying fields either flat or under payload depending on the stream layer,
                // so read from whichever is present. No errorText so nothing renders red, and no
                // declineReason is stuffed into the part.
                const deniedToolCallId = event.toolCallId ?? event.payload?.toolCallId;
                if (!deniedToolCallId) break;
                const deniedToolName = event.toolName ?? event.payload?.toolName;
                this.toolInputBuffers.delete(deniedToolCallId);
                this.pendingApprovalToolCalls.delete(deniedToolCallId);
                this.toolRunIds.delete(deniedToolCallId);
                this.updateToolPart(deniedToolCallId, {
                    ...(deniedToolName ? { toolName: deniedToolName } : {}),
                    state: 'output-denied',
                });
                break;
            }
            case 'tool-result':
                this.toolInputBuffers.delete(event.payload.toolCallId);
                this.pendingApprovalToolCalls.delete(event.payload.toolCallId);
                this.toolRunIds.delete(event.payload.toolCallId);
                this.updateToolPart(event.payload.toolCallId, {
                    toolName: event.payload.toolName,
                    state: event.payload.isError ? 'output-error' : 'output-available',
                    output: event.payload.result,
                    ...(event.payload.isError ? { errorText: this.extractErrorMessage(event.payload.result) } : {}),
                });
                break;
            case 'tool-error':
                this.toolInputBuffers.delete(event.payload.toolCallId);
                this.pendingApprovalToolCalls.delete(event.payload.toolCallId);
                this.toolRunIds.delete(event.payload.toolCallId);
                this.updateToolPart(event.payload.toolCallId, {
                    toolName: event.payload.toolName,
                    state: 'output-error',
                    errorText: this.extractErrorMessage(event.payload.error ?? 'Tool execution failed'),
                });
                break;
            case 'error':
                if (event.payload.code === AI_CHAT_CONTEXT_LIMIT_REACHED_CODE && isAIChatContextGuardStatus(event.payload.contextStatus)) {
                    this.rollbackRejectedTurn();
                    this.applyContextGuardStatus(event.payload.contextStatus);
                    this.clearError();
                    break;
                }
                if (event.payload.code === AI_PROVIDER_UNAVAILABLE_CODE) {
                    // Keep the transcript (including the just-sent user message) — the turn simply
                    // could not run. The host swaps the composer for the unavailable notice.
                    this.$providerUnavailable.set(true);
                    this.$status.set('idle');
                    this.clearError();
                    break;
                }
                this.$error.set(new Error(event.payload.message || 'Stream error'));
                break;
            case 'data-context-status':
                if (isAIChatContextGuardStatus(event.data)) {
                    this.applyContextGuardStatus(event.data);
                }
                break;
            case 'finish':
            case 'done':
                this.$status.set('idle');
                break;
            default:
                break;
        }
    }

    private readContextGuardStatus(messages: UIMessage[]): AIChatContextGuardStatus | null {

        for (let index = messages.length - 1; index >= 0; index -= 1) {
            const message = messages[index];
            if (message.role !== 'assistant') continue;
            const metadata = message.metadata as Record<string, unknown> | undefined;
            const status = metadata?.[AI_CHAT_CONTEXT_GUARD_METADATA_KEY];
            if (isAIChatContextGuardStatus(status)) return status;
        }
        return null;
    }

    private applyContextGuardStatus(status: AIChatContextGuardStatus): void {

        this.$contextGuardStatus.set(status);
        // Keep the warm in-memory thread cache aligned with the live measurement. The output
        // processor persists only the final status; this transient copy lets popup/fullscreen
        // switches retain the latest warning while the current request is still running.
        this.$messages.update(messages => {
            const next = [...messages];
            for (let index = next.length - 1; index >= 0; index -= 1) {
                const message = next[index];
                if (message.role !== 'assistant') continue;
                next[index] = {
                    ...message,
                    metadata: {
                        ...(message.metadata as Record<string, unknown> | undefined),
                        [AI_CHAT_CONTEXT_GUARD_METADATA_KEY]: status,
                    },
                } as UIMessage;
                return next;
            }
            return messages;
        });
    }

    /** Remove the optimistic user/assistant pair when the server rejects a stale-tab send.
     *  Neither message reached Mastra memory, so leaving either in the transcript would make the
     *  client disagree with the restored thread after a reload. */
    /** A pre-stream 429 from the usage-limit door check — distinguished from the endpoint's
     *  rate-limit throttler (also 429) by the `limit` object only the usage refusal carries. */
    private isUsageLimitRejection(error: unknown): boolean {

        const rejection = error as { status?: number; body?: { limit?: unknown } | null } | null;
        return rejection?.status === 429 && !!rejection?.body?.limit;
    }

    private rollbackRejectedTurn(): void {

        const rejectedIds = new Set([
            this.pendingTurnUserMessageId,
            this.pendingTurnAssistantMessageId,
        ].filter((id): id is string => Boolean(id)));
        if (rejectedIds.size > 0) {
            this.$messages.update(messages => messages.filter(message => !rejectedIds.has(message.id)));
        }
        this.pendingTurnUserMessageId = null;
        this.pendingTurnAssistantMessageId = null;
    }

    private appendTextPart(text: string): void {

        this.withLastAssistantMessage((message) => {
            const lastPart = message.parts[message.parts.length - 1];
            if (lastPart?.type === 'text') {
                const current = AgentUtility.partField<string>(lastPart, 'text', '');
                message.parts[message.parts.length - 1] = { ...lastPart, text: current + text } as AnyPart;
                return;
            }
            message.parts.push({ type: 'text', text } as AnyPart);
        });
    }

    private appendTextDelta(delta: string): void {

        if (!delta) return;
        this.withLastAssistantMessage((message) => {
            const lastPart = message.parts[message.parts.length - 1];
            if (lastPart?.type === 'text') {
                const current = AgentUtility.partField<string>(lastPart, 'text', '');
                message.parts[message.parts.length - 1] = { ...lastPart, text: current + delta } as AnyPart;
            } else {
                message.parts.push({ type: 'text', text: delta } as AnyPart);
            }
        });
    }

    private appendReasoningPart(text: string): void {

        this.withLastAssistantMessage((message) => {
            message.parts.push({ type: 'reasoning', text } as AnyPart);
        });
    }

    private appendReasoningDelta(delta: string): void {

        if (!delta) return;
        this.withLastAssistantMessage((message) => {
            const lastPart = message.parts[message.parts.length - 1];
            if (lastPart?.type === 'reasoning') {
                const current = AgentUtility.partField<string>(lastPart, 'text', '');
                message.parts[message.parts.length - 1] = { ...lastPart, text: current + delta } as AnyPart;
            } else {
                message.parts.push({ type: 'reasoning', text: delta } as AnyPart);
            }
        });
    }

    private updateToolPart(toolCallId: string, patch: Partial<AnyPart> & { state?: string; toolName?: string; input?: unknown; output?: unknown; errorText?: string; approval?: ToolApprovalState }): void {

        this.withLastAssistantMessage((message) => {
            const partIndex = message.parts.findIndex(part => AgentUtility.partField(part, 'toolCallId', '') === toolCallId);
            const nextPart = {
                type: 'dynamic-tool',
                toolCallId,
                toolName: patch.toolName ?? AgentUtility.partField(message.parts[partIndex] ?? ({ type: 'dynamic-tool' } as AnyPart), 'toolName', ''),
                state: patch.state ?? AgentUtility.partField(message.parts[partIndex] ?? ({ type: 'dynamic-tool' } as AnyPart), 'state', ''),
                input: patch.input ?? AgentUtility.partField(message.parts[partIndex] ?? ({ type: 'dynamic-tool' } as AnyPart), 'input', undefined),
                output: patch.output ?? AgentUtility.partField(message.parts[partIndex] ?? ({ type: 'dynamic-tool' } as AnyPart), 'output', undefined),
                errorText: patch.errorText ?? AgentUtility.partField(message.parts[partIndex] ?? ({ type: 'dynamic-tool' } as AnyPart), 'errorText', ''),
                approval: patch.approval ?? AgentUtility.partField(message.parts[partIndex] ?? ({ type: 'dynamic-tool' } as AnyPart), 'approval', undefined),
            } as AnyPart;

            if (partIndex >= 0) {
                message.parts[partIndex] = { ...(message.parts[partIndex] as AnyPart), ...nextPart } as AnyPart;
            } else {
                message.parts.push(nextPart);
            }
        });
    }

    private hasOutputForToolCall(toolCallId: string): boolean {

        const messages = this.$messages();
        for (let i = messages.length - 1; i >= 0; i -= 1) {
            const message = messages[i];
            if (message.role !== 'assistant') continue;
            const toolPart = message.parts?.find(part => AgentUtility.partField(part, 'toolCallId', '') === toolCallId);
            if (!toolPart) continue;
            const state = AgentUtility.partField<string>(toolPart, 'state', '');
            return state === 'output-available' || state === 'output-error' || state === 'output-denied';
        }
        return false;
    }

    private withLastAssistantMessage(mutator: (message: UIMessage) => void): void {

        this.$messages.update((messages) => {
            const next = messages.map(message => this.cloneMessage(message));
            let assistantIndex = -1;
            for (let i = next.length - 1; i >= 0; i -= 1) {
                if (next[i].role === 'assistant') {
                    assistantIndex = i;
                    break;
                }
            }
            if (assistantIndex === -1) {
                next.push(this.createAssistantPlaceholder());
                assistantIndex = next.length - 1;
            }
            mutator(next[assistantIndex]);
            return next;
        });
    }

    private cleanupEmptyAssistantPlaceholder(): void {

        this.$messages.update((messages) => {
            const next = messages.map(message => this.cloneMessage(message));
            const assistantIndex = [...next.keys()].reverse().find(index => next[index].role === 'assistant');
            if (assistantIndex === undefined) return next;
            const assistant = next[assistantIndex];
            if ((assistant.parts?.length ?? 0) > 0) return next;
            next.splice(assistantIndex, 1);
            return next;
        });
    }

    private createAssistantPlaceholder(): UIMessage {

        return { id: v4(), role: 'assistant', parts: [], createdAt: new Date() } as UIMessage;
    }

    private cloneMessage(message: UIMessage): UIMessage {

        return {
            ...message,
            parts: Array.isArray(message.parts) ? message.parts.map(part => ({ ...part } as AnyPart)) : [],
        };
    }

    private resolveRunId(toolCallId: string): string | null {

        return this.toolRunIds.get(toolCallId) ?? this.currentRunId;
    }

    private restorePendingApprovalRunIds(messages: UIMessage[]): void {

        this.pendingApprovalToolCalls.clear();
        this.toolRunIds.clear();
        this.currentRunId = null;

        for (const message of messages) {
            if (message.role !== 'assistant') continue;

            for (const part of message.parts ?? []) {
                const state = AgentUtility.partField<string>(part as AnyPart, 'state', '');
                if (state !== 'approval-requested') continue;

                const toolCallId = AgentUtility.partField(part as AnyPart, 'toolCallId', '');
                if (!toolCallId) continue;

                const approval = AgentUtility.partField<ToolApprovalState | null>(part as AnyPart, 'approval', null);
                this.pendingApprovalToolCalls.add(toolCallId);
                if (!approval?.runId) continue;

                this.toolRunIds.set(toolCallId, approval.runId);
                this.currentRunId ??= approval.runId;
            }
        }
    }

    private parseToolInput(buffer: string): unknown {

        const trimmed = buffer.trim();
        if (!trimmed) return {};
        try {
            return JSON.parse(trimmed);
        } catch {
            return trimmed;
        }
    }

    private extractErrorMessage(result: unknown): string {

        if (result instanceof Error) return result.message;
        if (typeof result === 'string') return result;
        if (result && typeof result === 'object') {
            const candidate = result as { message?: string; error?: string; errorText?: string };
            return candidate.message || candidate.error || candidate.errorText || 'Tool execution failed';
        }
        return 'Tool execution failed';
    }

    private extractAttachedFiles(message: UIMessage, blobUrlMap?: Record<string, string>): AIAgentAttachment[] {

        if (message.role !== 'user') return [];

        return (message.parts ?? [])
            .filter((part) => part.type === 'file')
            .map((part) => {
                const filename = AgentUtility.resolveFilename(part as AnyPart);
                const idMatch = filename.match(/^(att_[a-zA-Z0-9_-]+)__/);
                const id = idMatch ? idMatch[1] : undefined;
                const url = AgentUtility.partField<string>(part as AnyPart, 'url', '');
                // Some files (notably .msg on macOS) upload with a blank MIME type. Derive one from
                // the filename extension instead of dropping the file, so it still hydrates into the
                // sandbox uploads/ directory and the agent can read it.
                const mediaType = AgentUtility.partField<string>(part as AnyPart, 'mediaType', '') || this.inferMediaType(filename);
                if (!id || !url) return null;
                const blobUrl = blobUrlMap?.[id];
                return { id, url, mediaType, ...(filename ? { filename } : {}), ...(blobUrl ? { blobUrl } : {}) };
            })
            .filter((file): file is NonNullable<typeof file> => Boolean(file));
    }

    private inferMediaType(filename: string): string {

        const lower = filename.toLowerCase();
        if (lower.endsWith('.msg')) return 'application/vnd.ms-outlook';
        if (lower.endsWith('.eml')) return 'message/rfc822';
        return 'application/octet-stream';
    }

    private abortActiveStream(): void {

        if (this.activeAbortController && !this.activeAbortController.signal.aborted) {
            this.resolvingAbort = true;
            this.activeAbortController.abort();
        }
        this.activeSubscription?.unsubscribe();
        this.activeSubscription = null;
        this.activeAbortController = null;
        const resolve = this.activeCompletion;
        this.activeCompletion = null;
        resolve?.();
    }

    private detachActiveStream(): void {

        this.activeSubscription?.unsubscribe();
        this.activeSubscription = null;
        this.activeAbortController = null;
        const resolve = this.activeCompletion;
        this.activeCompletion = null;
        resolve?.();
    }

    private isAbortRelatedError(error: unknown): boolean {

        if (error instanceof Error) {
            const msg = error.message.toLowerCase();
            return error.name === 'AbortError' || msg.includes('aborted') || msg.includes('cancelled') || msg.includes('body stream');
        }
        return false;
    }
}
